import { IkeUnitDistance } from '@ikegps/core-api';
import { FormModelField } from '../form/form.field';
import type { CollectionModelBase } from './collection.base';
import type { CollectionModel } from './collection.data';
import type { ModelFieldState } from './collection.state';

export abstract class ModelMutator<T extends FormModelField, U extends CollectionModelField<T>> {
	field: U;

	constructor(field: U) {
		this.field = field;
	}

	get state() {
		return this.field.collection.state;
	}

	/** Removes the field value. */
	clear() {
		this.state.setFieldValue(this.field.id, null);
	}

	/** Copies from the target into this Collection. */
	copy(from: U): boolean {
		let stateValue = from.stateValue;
		if (stateValue != null) {
			stateValue = JSON.parse(JSON.stringify(stateValue));
		}

		this.state.setFieldValue(this.field.id, stateValue ?? null);

		return true;
	}

	truncatedCopy(from: U): boolean {
		return true;
	}
}

export abstract class CollectionModelField<
	// Type of the field schema
	FieldType extends FormModelField = FormModelField,
	// Return type of .stateValue
	StateValue extends ModelFieldState = ModelFieldState,
	// return type of .value
	RawValue = any,
> {
	readonly parent: CollectionModelBase;
	/**
	 * Collection unique field id, this will contain the subform instanceId and field id
	 * Examples:
	 * - `:poleId` - Top level field
	 * - `Lmj5Mkj1z:spanLength` - SubformId:fieldId
	 */
	readonly id: string;
	readonly schema: FieldType;
	readonly _value: RawValue;

	protected _distanceUnit: IkeUnitDistance = IkeUnitDistance.FEET;
	public get distanceUnit(): IkeUnitDistance {
		return this._distanceUnit;
	}
	public set distanceUnit(value: IkeUnitDistance) {
		this._distanceUnit = value;
	}

	get value(): RawValue {
		return this._value;
	}

	abstract Mutator: ModelMutator<FieldType, CollectionModelField<FieldType, StateValue, RawValue>>;

	/**
	 * Fields are stored in a hash map, they need to be unique.
	 * Using the subformInstanceId and the field's schema id creates a unique reference.
	 */
	static getStateId(parent: CollectionModelBase, schemaId: string): string {
		return parent.instanceId + ':' + schemaId;
	}

	constructor(parent: CollectionModelBase, schema: FieldType) {
		this.parent = parent;
		this.schema = schema;
		this.id = CollectionModelField.getStateId(this.parent, this.schema.id);
	}

	/** Traverse up the tree to find the root collection for this field */
	get collection(): CollectionModel {
		return this.parent.collection;
	}

	/** Raw state value that is stored for this field */
	get stateValue(): StateValue | undefined {
		return this.collection.state.getFieldValue(this.id) as StateValue | undefined;
	}

	get displayValue(): string {
		return String(this.stateValue);
	}

	get annotationLabel(): string {
		return this.parent.title;
	}

	protected findAncestorField(node: CollectionModelBase, fieldId: string): CollectionModelField | undefined {
		if (node.hasField(fieldId)) {
			return node.getField(fieldId);
		}
		if (!node.parent || !node.parent.parent) {
			return undefined;
		}
		return this.findAncestorField(node.parent.parent, fieldId);
	}

	abstract hasValue(): boolean;
}
